import { useCallback, useState, KeyboardEvent } from 'react'
import { Input } from '../ui/Input'
import { Button } from '../ui/Button'
import { CVLabelInput } from './CVLabelInput'
import { useTranslation } from '../../contexts/LanguageContext'
import type { Skills } from '../../types/cv'

interface SkillsFormProps {
  data: Skills | undefined
  onChange: (skills: Partial<Skills>) => void
  customLabels?: { skills?: string; technical?: string; soft?: string; tools?: string }
  onCustomLabelChange: (key: string, value: string) => void
}

type SkillCategory = 'technical' | 'soft' | 'tools'

interface SkillInputProps {
  label: string
  placeholder: string
  skills: string[]
  addLabel: string
  removeAriaLabel: string
  onAdd: (skill: string) => void
  onRemove: (index: number) => void
}

function SkillInput({
  label,
  placeholder,
  skills,
  addLabel,
  removeAriaLabel,
  onAdd,
  onRemove,
}: SkillInputProps) {
  const [inputValue, setInputValue] = useState('')

  const handleKeyDown = (e: KeyboardEvent<HTMLInputElement>) => {
    if (e.key === 'Enter' && inputValue.trim()) {
      e.preventDefault()
      onAdd(inputValue.trim())
      setInputValue('')
    }
  }

  const handleAddClick = () => {
    if (inputValue.trim()) {
      onAdd(inputValue.trim())
      setInputValue('')
    }
  }

  return (
    <div>
      <label className="block text-sm font-semibold text-slate-700 mb-1.5">
        {label}
      </label>
      <div className="flex gap-2 mb-2">
        <Input
          placeholder={placeholder}
          value={inputValue}
          onChange={(e) => setInputValue(e.target.value)}
          onKeyDown={handleKeyDown}
          className="flex-1"
        />
        <Button
          type="button"
          variant="secondary"
          size="md"
          onClick={handleAddClick}
          disabled={!inputValue.trim()}
        >
          {addLabel}
        </Button>
      </div>
      <div className="flex flex-wrap gap-2">
        {skills.map((skill, index) => (
          <span
            key={`${skill}-${index}`}
            className="inline-flex items-center gap-1 px-3 py-1 bg-slate-100 text-slate-700 rounded-full text-sm"
          >
            {skill}
            <button
              type="button"
              onClick={() => onRemove(index)}
              className="ml-1 text-slate-400 hover:text-red-500 transition-colors"
              aria-label={`${removeAriaLabel} ${skill}`}
            >
              <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
              </svg>
            </button>
          </span>
        ))}
      </div>
    </div>
  )
}

export function SkillsForm({ data, onChange, customLabels, onCustomLabelChange }: SkillsFormProps) {
  const { t } = useTranslation()

  const addSkill = useCallback(
    (category: SkillCategory, skill: string) => {
      const currentSkills = data?.[category] || []
      if (!currentSkills.includes(skill)) {
        onChange({ [category]: [...currentSkills, skill] })
      }
    },
    [data, onChange]
  )

  const removeSkill = useCallback(
    (category: SkillCategory, index: number) => {
      const currentSkills = data?.[category] || []
      onChange({
        [category]: currentSkills.filter((_, i) => i !== index),
      })
    },
    [data, onChange]
  )

  return (
    <div className="space-y-6">
      <CVLabelInput value={customLabels?.skills} placeholder="Skills" onChange={(v) => onCustomLabelChange('skills', v)} />
      <SkillInput
        label={t.skillsForm.technical}
        placeholder={t.skillsForm.technicalPlaceholder}
        skills={data?.technical || []}
        addLabel={t.skillsForm.add}
        removeAriaLabel={t.skillsForm.removeAria}
        onAdd={(skill) => addSkill('technical', skill)}
        onRemove={(index) => removeSkill('technical', index)}
      />

      <SkillInput
        label={t.skillsForm.soft}
        placeholder={t.skillsForm.softPlaceholder}
        skills={data?.soft || []}
        addLabel={t.skillsForm.add}
        removeAriaLabel={t.skillsForm.removeAria}
        onAdd={(skill) => addSkill('soft', skill)}
        onRemove={(index) => removeSkill('soft', index)}
      />

      <SkillInput
        label={t.skillsForm.tools}
        placeholder={t.skillsForm.toolsPlaceholder}
        skills={data?.tools || []}
        addLabel={t.skillsForm.add}
        removeAriaLabel={t.skillsForm.removeAria}
        onAdd={(skill) => addSkill('tools', skill)}
        onRemove={(index) => removeSkill('tools', index)}
      />
    </div>
  )
}
